"use client";

import { useEffect, useRef, useState } from "react";
import { Database, TrendingUp, Sparkles, MessageSquare } from "lucide-react";

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

export const FeaturesSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const features: Feature[] = [
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "AI Growth Hacker",
      description: "Let AI analyze your app's performance and suggest data-driven growth strategies tailored to your users' behavior patterns."
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: "Connect Your Supabase",
      description: "Securely connect in seconds with OAuth. We use read-only queries to analyze your data—you stay in full control. Disconnect anytime from your Supabase dashboard."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Explore Growth Metrics",
      description: "Dive into comprehensive analytics covering user acquisition, retention, engagement, and conversion funnels with real-time dashboards."
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "Smart Question Suggestions",
      description: "Get intelligent, context-aware question recommendations based on your data patterns to uncover hidden growth opportunities."
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.2,
        rootMargin: "0px"
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="min-h-screen py-24 px-4 sm:px-6 md:px-12 lg:px-20 bg-gradient-to-b from-background to-background/50"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2
            className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Your Growth,{" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent">
              Supercharged
            </span>
          </h2>
          <p
            className={`text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto transition-all duration-700 delay-100 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Harness the power of AI-driven analytics to accelerate your app's growth and make data-backed decisions with confidence.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`group relative p-8 rounded-2xl border border-border bg-card/50 backdrop-blur-sm hover:bg-card transition-all duration-700 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{
                transitionDelay: `${200 + index * 100}ms`
              }}
            >
              {/* Glow effect on hover */}
              <div className="absolute -inset-0.5 bg-gradient-primary rounded-2xl opacity-0 group-hover:opacity-20 blur transition duration-300" />

              <div className="relative">
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-primary/10 text-primary mb-6 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold mb-4 text-foreground group-hover:text-primary transition-colors duration-300">
                  {feature.title}
                </h3>

                {/* Description */}
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
